import { useState, type ReactNode } from 'react';
import { Icon, type IconName } from '@capsico/ui';
import type { AddressView, BillLine, CountryCode, OfferView, QuoteLine } from '@capsico/contracts';
import { money } from '../utils/money';
import { Button, Card, Pill, Sheet, VegMark } from './ui';

/*
 * Checkout building blocks (C13–C18, capsico-customer-app-ui). Presentation only: every value
 * and handler comes from useCheckout through the page.
 */

/** C13 cart line: veg mark, name, size + add-ons, note, − n + stepper, line total, issue in red. */
export function CartLineRow({
  line,
  country,
  quantity,
  onIncrease,
  onDecrease,
  canIncrease,
}: {
  line: QuoteLine;
  country: CountryCode;
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
  canIncrease: boolean;
}) {
  const details = [
    line.variantName,
    ...line.addons.map((addon) => addon.name),
    ...line.comboChoices,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <div className="flex items-start gap-3 py-3">
      <span className="pt-1">
        <VegMark type={line.foodType} size={12} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium text-ink">{line.name}</p>
        {details && <p className="mt-0.5 line-clamp-2 text-caption text-ink-3">{details}</p>}
        {line.note && <p className="mt-0.5 text-caption text-ink-2">“{line.note}”</p>}
        {line.issue && <p className="mt-1 text-caption font-medium text-negative">{line.issue}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <div className="flex h-8 w-22 items-center justify-between rounded-lg bg-brand px-1 text-on-brand">
          <button
            type="button"
            aria-label={`− ${line.name}`}
            onClick={onDecrease}
            className="flex size-7 items-center justify-center"
          >
            <Icon name="minus" size={14} />
          </button>
          <span className="text-sm font-bold tabular">{quantity}</span>
          <button
            type="button"
            aria-label={`+ ${line.name}`}
            onClick={onIncrease}
            disabled={!canIncrease}
            className="flex size-7 items-center justify-center disabled:opacity-50"
          >
            <Icon name="plus" size={14} />
          </button>
        </div>
        <span className="text-sm font-semibold text-ink tabular">
          {money(country, line.lineTotalMinor)}
        </span>
      </div>
    </div>
  );
}

/** A tappable row with a leading icon tile and chevron (used for offers, address, help). */
export function ActionRow({
  icon,
  title,
  subtitle,
  trailing,
  onClick,
}: {
  icon: IconName;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
        <Icon name={icon} size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-ink">{title}</span>
        {subtitle && <span className="block text-caption text-ink-2">{subtitle}</span>}
      </span>
      {trailing ?? (onClick && <Icon name="chevron-right" size={16} />)}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 text-left">
      {body}
    </button>
  ) : (
    <div className="flex w-full items-center gap-3">{body}</div>
  );
}

/** C14 bill: every line with its note, struck price and a "why" info button; "To pay" total. */
export function BillCard({
  title,
  lines,
  total,
  totalLabel,
  savings,
  country,
  fundingLabel,
  whyLabel,
  onWhy,
}: {
  title: string;
  lines: BillLine[];
  total: number;
  totalLabel: string;
  savings: string | null;
  country: CountryCode;
  fundingLabel: (funding: NonNullable<BillLine['funding']>) => string;
  whyLabel: string;
  onWhy: (line: BillLine) => void;
}) {
  return (
    <Card>
      <h2 className="text-sm font-bold text-ink">{title}</h2>
      <div className="mt-3 space-y-2.5">
        {lines.map((line) => {
          const negative = line.amountMinor < 0;
          const green = negative || (line.amountMinor === 0 && line.struckMinor !== null);
          return (
            <div key={line.key} className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 text-xs text-ink-2">
                  <span className="truncate">{line.label}</span>
                  {line.why && (
                    <button
                      type="button"
                      aria-label={`${whyLabel} ${line.label}`}
                      onClick={() => onWhy(line)}
                      className="inline-flex size-5 shrink-0 items-center justify-center text-ink-3"
                    >
                      <Icon name="info" size={13} />
                    </button>
                  )}
                </p>
                {line.note && <p className="text-caption text-ink-3">{line.note}</p>}
                {line.funding && line.key === 'offer' && (
                  <p className="text-caption font-medium text-positive">
                    {fundingLabel(line.funding)}
                  </p>
                )}
              </div>
              <p className="shrink-0 text-xs tabular">
                {line.struckMinor !== null && (
                  <span className="mr-1.5 text-ink-3 line-through">
                    {money(country, line.struckMinor)}
                  </span>
                )}
                <span className={green ? 'font-semibold text-positive' : 'text-ink'}>
                  {negative
                    ? `−${money(country, -line.amountMinor)}`
                    : money(country, line.amountMinor)}
                </span>
              </p>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between pt-3 shadow-[inset_0_1px_0_var(--color-hair)]">
        <span className="text-sm font-bold text-ink">{totalLabel}</span>
        <span className="text-base font-bold text-ink tabular">{money(country, total)}</span>
      </div>
      {savings && (
        <p className="mt-3 rounded-lg bg-positive-bg px-3 py-2 text-center text-xs font-semibold text-positive">
          {savings}
        </p>
      )}
    </Card>
  );
}

/** "Why this fee?" sheet: plain words about one bill line. */
export function WhySheet({
  line,
  closeLabel,
  onClose,
}: {
  line: BillLine;
  closeLabel: string;
  onClose: () => void;
}) {
  return (
    <Sheet
      title={line.label}
      onClose={onClose}
      footer={
        <Button full onClick={onClose}>
          {closeLabel}
        </Button>
      }
    >
      <p className="text-sm leading-6 text-ink-2">{line.why}</p>
    </Sheet>
  );
}

/** C15 offers sheet: code box + Apply, then every listed offer with its saving or the reason it does not apply. */
export function OffersSheet({
  offers,
  country,
  applied,
  error,
  labels,
  onApply,
  onRemove,
  onClose,
}: {
  offers: OfferView[];
  country: CountryCode;
  applied: string | null;
  error: string | null;
  labels: {
    title: string;
    enterCode: string;
    apply: string;
    remove: string;
    save: (amount: string) => string;
    funding: (funding: OfferView['fundedBy']) => string;
  };
  onApply: (code: string) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  return (
    <Sheet title={labels.title} onClose={onClose}>
      <div className="flex items-center gap-2">
        <input
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          placeholder={labels.enterCode}
          aria-label={labels.enterCode}
          maxLength={20}
          className="h-11 min-w-0 flex-1 rounded-field bg-surface px-3 text-sm font-semibold tracking-wide text-ink uppercase placeholder:font-normal placeholder:normal-case placeholder:text-ink-3 shadow-[inset_0_0_0_1px_var(--color-field)] focus:outline-none focus:shadow-[inset_0_0_0_1.5px_var(--color-brand-600)]"
        />
        <Button size="sm" disabled={code.trim().length < 3} onClick={() => onApply(code)}>
          {labels.apply}
        </Button>
      </div>
      {error && <p className="mt-2 text-caption text-negative">{error}</p>}
      <div className="mt-4 space-y-3">
        {offers.map((offer) => {
          const isApplied = offer.code === applied;
          return (
            <div
              key={offer.id}
              className={`rounded-xl p-3.5 ${
                isApplied
                  ? 'bg-brand-soft shadow-[inset_0_0_0_1.5px_var(--color-brand-600)]'
                  : 'bg-surface shadow-[inset_0_0_0_1px_var(--color-hair)]'
              } ${offer.eligible ? '' : 'opacity-70'}`}
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand text-on-brand">
                  <Icon name="tag" size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-ink">{offer.code}</p>
                  <p className="text-xs text-ink-2">{offer.title}</p>
                  {offer.description && (
                    <p className="mt-0.5 text-caption text-ink-3">{offer.description}</p>
                  )}
                  <p className="mt-1 text-caption text-ink-3">{labels.funding(offer.fundedBy)}</p>
                  {offer.eligible
                    ? offer.discountMinor > 0 && (
                        <p className="mt-1 text-caption font-semibold text-positive">
                          {labels.save(money(country, offer.discountMinor))}
                        </p>
                      )
                    : offer.reason && (
                        <p className="mt-1 text-caption font-medium text-warning">{offer.reason}</p>
                      )}
                </div>
                {isApplied ? (
                  <button
                    type="button"
                    onClick={onRemove}
                    className="text-xs font-semibold text-negative"
                  >
                    {labels.remove}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!offer.eligible}
                    onClick={() => onApply(offer.code)}
                    className="text-xs font-bold text-brand-ink disabled:text-ink-3"
                  >
                    {labels.apply}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}

/** C16 tip: preset amounts as pills plus "No tip". */
export function TipCard({
  title,
  hint,
  noTipLabel,
  presets,
  value,
  country,
  onChange,
}: {
  title: string;
  hint: string;
  noTipLabel: string;
  presets: number[];
  value: number;
  country: CountryCode;
  onChange: (minor: number) => void;
}) {
  return (
    <Card>
      <h2 className="text-sm font-bold text-ink">{title}</h2>
      <p className="mt-0.5 text-caption text-ink-3">{hint}</p>
      <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label={title}>
        <Pill active={value === 0} onClick={() => onChange(0)}>
          {noTipLabel}
        </Pill>
        {presets.map((preset) => (
          <Pill key={preset} active={value === preset} onClick={() => onChange(preset)}>
            {money(country, preset)}
          </Pill>
        ))}
      </div>
    </Card>
  );
}

/** C17 address sheet: saved addresses as a radio list (unserviceable ones disabled) + Add new. */
export function AddressSheet({
  addresses,
  selectedId,
  labels,
  title,
  addLabel,
  notServed,
  onPick,
  onAdd,
  onClose,
}: {
  addresses: AddressView[];
  selectedId: string | null;
  labels: Record<'home' | 'work' | 'other', string>;
  title: string;
  addLabel: string;
  notServed: string;
  onPick: (id: string) => void;
  onAdd: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <Button full tone="outline" onClick={onAdd}>
          <Icon name="plus" size={16} />
          {addLabel}
        </Button>
      }
    >
      <div className="divide-y divide-hair">
        {addresses.map((address) => {
          const on = address.id === selectedId;
          const name =
            address.label === 'other' ? (address.labelText ?? labels.other) : labels[address.label];
          return (
            <button
              key={address.id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!address.serviceable}
              onClick={() => onPick(address.id)}
              className="flex w-full items-start gap-3 py-3 text-left disabled:opacity-50"
            >
              <Icon
                name={
                  address.label === 'home' ? 'home' : address.label === 'work' ? 'store' : 'pin'
                }
                size={18}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-ink">{name}</span>
                <span className="block line-clamp-2 text-caption text-ink-2">
                  {[address.house, address.floor, address.area].filter(Boolean).join(', ')}
                </span>
                {!address.serviceable && (
                  <span className="block text-caption font-medium text-negative">{notServed}</span>
                )}
              </span>
              <span
                className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full ${
                  on
                    ? 'bg-brand shadow-[inset_0_0_0_1.5px_var(--color-ink)]'
                    : 'shadow-[inset_0_0_0_1.5px_var(--color-control)]'
                }`}
              >
                {on && <span className="size-2 rounded-full bg-ink" />}
              </span>
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}

/** Pinned bottom bar: amount + one action (pattern C grammar). */
export function PayBar({
  amount,
  caption,
  children,
}: {
  amount: string | null;
  caption: string | null;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-120 bg-surface px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)] shadow-[inset_0_1px_0_var(--color-hair),0_-4px_12px_rgba(15,23,42,0.06)]">
      <div className="flex items-center gap-3">
        {amount && (
          <div className="shrink-0">
            <p className="text-base font-bold text-ink tabular">{amount}</p>
            {caption && <p className="text-caption text-ink-3">{caption}</p>}
          </div>
        )}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}
